import { useEffect } from 'react';
import { X, ArrowLeft, Plus } from 'lucide-react';
import { useSupport } from '@/contexts/SupportContext';
import { SupportTicketList } from './SupportTicketList';
import { SupportNewRequest } from './SupportNewRequest';
import { SupportThread } from './SupportThread';
import { cn } from '@/lib/utils';

export function SupportSlideOver() {
  const { isOpen, view, selectedTicket, close, openNewRequest, openSupport } = useSupport();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const getTitle = () => {
    if (view === 'new') return 'New Request';
    if (view === 'thread' && selectedTicket) return `Request #${selectedTicket.id}`;
    return 'Support';
  };

  const showBack = view === 'new' || view === 'thread';

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={close}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-white shadow-xl',
          'transform transition-transform duration-300 ease-in-out flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {showBack && (
              <button
                onClick={openSupport}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900">{getTitle()}</h2>
          </div>
          <div className="flex items-center gap-1">
            {view === 'list' && (
              <button
                onClick={openNewRequest}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                New
              </button>
            )}
            <button
              onClick={close}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {view === 'list' && <SupportTicketList />}
          {view === 'new' && <SupportNewRequest />}
          {view === 'thread' && <SupportThread />}
        </div>
      </div>
    </>
  );
}
